import React from "react";
import AppShell from "../components/AppShell";
import { Droplet, Clock, Sparkles, Heart } from "lucide-react";

const BENEFITS = [
  "Mejorar el rendimiento físico durante los entrenamientos.",
  "Favorecer la recuperación muscular.",
  "Reducir la sensación de fatiga.",
  "Mejorar la digestión.",
  "Ayudar al control del apetito.",
  "Mantener la piel saludable y verte más joven.",
  "Favorecer el metabolismo.",
  "Mejorar la concentración y el estado de ánimo.",
  "Evitar dolores de cabeza relacionados con la deshidratación.",
];

const SCHEDULE = [
  { time: "7:00 AM", ml: "250 ml", note: "Un vaso de agua tibia con pizca de sal y 1 cc de vinagre de manzana" },
  { time: "8:00 AM", ml: "250 ml", note: "Un vaso de agua" },
  { time: "9:00 AM", ml: "250 ml", note: "Un vaso de agua" },
  { time: "11:00 AM", ml: "500 ml", note: "Dos vasos de agua" },
  { time: "1:00 PM", ml: "250 ml", note: "Un vaso de agua" },
  { time: "3:00 PM", ml: "500 ml", note: "Dos vasos de agua" },
  { time: "5:00 PM", ml: "250 ml", note: "Un vaso de agua" },
  { time: "7:00 PM", ml: "250 ml", note: "Un vaso de agua" },
  { time: "9:00 PM", ml: "250 ml", note: "Un vaso de agua" },
  { time: "10:00 PM", ml: "250 ml", note: "Puede ser té de manzanilla o menta con pizca de miel" },
];

export default function Hydration() {
  return (
    <AppShell title="Hidratación" subtitle="Tu mejor aliada en este cambio.">
      <div className="space-y-8 max-w-5xl" data-testid="hydration-page">

        {/* HERO */}
        <section className="relative overflow-hidden rounded-2xl border border-[var(--mep-border)]">
          <div className="grid md:grid-cols-2">
            <div className="relative aspect-[4/3] md:aspect-auto md:min-h-[360px]">
              <img
                src="https://images.pexels.com/photos/416528/pexels-photo-416528.jpeg?auto=compress&cs=tinysrgb&w=1200"
                alt="Vaso de agua fría premium"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-black/70 via-black/20 to-transparent" />
            </div>

            <div className="p-7 md:p-10 flex flex-col justify-center bg-gradient-to-br from-[#0d0d0f] via-[#0a0a0c] to-[#11090d]">
              <div className="w-12 h-12 rounded-full bg-[rgba(232,61,132,0.12)] border border-[var(--mep-pink)]/40 flex items-center justify-center text-[var(--mep-pink)] mb-4">
                <Droplet size={22} />
              </div>
              <div className="text-eyebrow text-[var(--mep-gold)] mb-3">El pilar del cambio</div>
              <h2 className="font-display text-2xl md:text-3xl font-light tracking-tight leading-snug">
                Tomar agua es lo más importante en esta nueva etapa de cambio en tu vida.
              </h2>
              <p className="text-sm text-[var(--mep-text-muted)] mt-4 leading-relaxed">
                Recuerda que muchas veces la sensación de hambre puede ser simplemente sed.
              </p>
            </div>
          </div>
        </section>

        {/* BENEFICIOS */}
        <section className="mep-card p-7 md:p-10" data-testid="hydration-benefits">
          <div className="flex items-center gap-3 mb-5">
            <Sparkles size={18} className="text-[var(--mep-gold)]" />
            <h3 className="font-display text-xl md:text-2xl font-light tracking-tight">Una buena hidratación puede ayudarte a:</h3>
          </div>
          <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {BENEFITS.map((b, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-[var(--mep-text-muted)] leading-relaxed">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--mep-pink)] flex-shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* CUÁNTA AGUA */}
        <section className="mep-card p-7 md:p-10" data-testid="hydration-schedule">
          <div className="flex items-center gap-3 mb-3">
            <Clock size={18} className="text-[var(--mep-pink)]" />
            <h3 className="font-display text-xl md:text-2xl font-light tracking-tight">¿Cuánta agua debo tomar?</h3>
          </div>
          <p className="text-sm text-[var(--mep-text-muted)] mb-2 leading-relaxed">
            Se recomienda consumir un mínimo de <span className="text-[var(--mep-gold)] font-medium">3 litros de agua al día</span>.
          </p>
          <p className="text-sm text-[var(--mep-text-muted)] mb-6 leading-relaxed">
            Para ayudarte a crear el hábito, puedes programar alarmas en tu teléfono utilizando el siguiente horario sugerido:
          </p>

          <div className="grid sm:grid-cols-2 gap-3">
            {SCHEDULE.map((s, i) => (
              <div
                key={i}
                className="flex items-start gap-4 p-4 rounded-lg bg-[var(--mep-surface-2)] border-l-2 border-[var(--mep-gold)]/40"
                data-testid={`hydration-row-${i}`}
              >
                <div className="font-display text-sm font-medium text-[var(--mep-gold)] tracking-wide w-20 flex-shrink-0">
                  {s.time}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[var(--mep-text)]">{s.ml}</div>
                  <div className="text-xs text-[var(--mep-text-muted)] mt-1 leading-relaxed">{s.note}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 p-4 rounded-lg bg-[rgba(212,175,55,0.10)] border-l-2 border-[var(--mep-gold)] text-sm">
            <span className="text-eyebrow text-[var(--mep-gold)]">Total aproximado: </span>
            <span className="text-[var(--mep-text)] font-medium">3 litros diarios.</span>
          </div>
        </section>

        {/* MOTIVACIONAL */}
        <section className="mep-card p-7 md:p-10 text-center" data-testid="hydration-motivational">
          <Heart size={22} className="text-[var(--mep-pink)] mx-auto mb-4" />
          <p className="font-display text-lg md:text-xl font-light tracking-tight leading-relaxed max-w-2xl mx-auto text-[var(--mep-text)]">
            Pequeñas acciones repetidas cada día crean grandes cambios con el tiempo.
            <br className="hidden md:block" />
            Mantente hidratado, sé constante y disfruta el proceso.
          </p>
        </section>
      </div>
    </AppShell>
  );
}
